/**
 * Lecture des réglages du restaurant au même chemin que la tablette
 * (src/components/Restaurant.js:875-877) : `settings.*` et `informations.*`.
 *
 * La PWA lisait ces champs à la racine du document, où ils n'existent pas
 * forcément — la suspension des commandes, le mode à-la-carte et le nom du
 * restaurant restaient donc sans effet. La racine est conservée en repli pour
 * les documents qui dupliquent ces clés.
 */
import type { RestaurantInfo } from "@/hooks/useRestaurant";

export const getRestaurantName = (resto: RestaurantInfo | null | undefined): string | undefined =>
  resto?.informations?.name ?? resto?.name ?? undefined;

export const isALaCarte = (resto: RestaurantInfo | null | undefined): boolean =>
  !!(resto?.settings?.aLaCarte ?? resto?.aLaCarte);

/** `settings.canOrder === false` = commandes suspendues (tablette : Cart.js:404). */
export const isOrderingSuspended = (resto: RestaurantInfo | null | undefined): boolean =>
  (resto?.settings?.canOrder ?? resto?.canOrder) === false;

export const isWaiterConfirmRequired = (resto: RestaurantInfo | null | undefined): boolean =>
  resto?.settings?.waiterConfirm === true;

/**
 * Mode consultation : `showSitInCart` (sur place) ou `showTakeAwayCart`
 * (à emporter) à faux masque panier, historique, compteurs et ajout.
 * Absent = affiché (défaut tablette : settingsReducer.js:50).
 */
export const isCartShown = (resto: RestaurantInfo | null | undefined, takeaway: boolean): boolean => {
  const flag = takeaway ? resto?.settings?.showTakeAwayCart : resto?.settings?.showSitInCart;
  return flag !== false;
};

export type CategoryLayout = "onePage" | "sidebar";

/**
 * Affichage des catégories (admin → Réglages tablette → Commande sur smartphone) : `sidebar` = colonne de
 * catégories à gauche ; absent ou inconnu = `onePage`, toutes les catégories à la suite (affichage historique).
 */
export const getCategoryLayout = (resto: RestaurantInfo | null | undefined): CategoryLayout =>
  resto?.settings?.smartphoneCategoryLayout === "sidebar" ? "sidebar" : "onePage";
